// events.ts is the remote storage.change subscription layer. It opens
// a single WebSocket per page (lazily on the first subscribe), fans
// inbound messages out to every registered handler, and reconnects
// with exponential backoff whenever the socket drops — unless the page's
// session has ended, which parks the loop instead (below).
//
// Contract: best-effort delivery. Events that arrive while no socket
// is connected (initial-connect failure, reconnect window, a park) are
// dropped on the floor; the caller can call data.list() / userdata.list()
// on the first event after a gap if they care about catching up. The
// payload's `crc32c` field gives callers a cheap way to detect
// duplicates if the underlying pub/sub ever delivers the same message
// twice.
//
// Session-aware reconnect. An upgrade the server rejects (401 for an
// expired, revoked or rotated-out session; also 429/503/421) never opens,
// and the browser hides its HTTP status — the socket just closes, usually
// with 1006 — so the socket alone can't tell "the session ended" from "the
// network blipped". Yet every rejected upgrade that resolved the glow is
// metered as one request against the glow owner's account-wide monthly pool
// (CLAUDE.md §9/§12), so retrying an ended session at the 30s backoff cap
// burns ~120 requests an hour per tab, forever. The classic trigger is a tab
// left hidden past the 7-day session (the heartbeat pauses while hidden, D4)
// whose socket then hits the hourly forced reconnect. So after
// PROBE_AFTER_FAILED_HANDSHAKES consecutive failed handshakes the bus probes
// the session with a heartbeat — unmetered, and a fetch CAN read the status.
// A 401 PARKS the loop (handlers stay registered; no socket, no timer) until a
// heartbeat succeeds again: the lifecycle's own hourly or →visible beats, or
// the bus's probe on each →visible transition (the D4 precedent). A page
// reload starts afresh. Any other answer keeps today's backoff.

import type { StorageChangeEvent, StorageChangeHandler, Unsubscribe } from "./backend.js";
import { onHeartbeatResult, sendHeartbeat } from "./heartbeat.js";

// Passive observers (backlit.offline, READ-6) see every frame a socket the app
// ALREADY opened delivers, ahead of the app's handlers. Registering one never
// opens, holds or reconnects a socket (it is not in `handlers`), so the
// metered /_backlit/events connection exists only while the app subscribes.
const passiveObservers = new Set<StorageChangeHandler>();

// observeStorageChanges registers fn as a passive observer; the returned
// unregister is idempotent.
export function observeStorageChanges(fn: StorageChangeHandler): Unsubscribe {
  passiveObservers.add(fn);
  return () => {
    passiveObservers.delete(fn);
  };
}

// Wire payload is JSON-identical to StorageChangeEvent; the parsing
// step exists mostly so a malformed frame from a buggy server doesn't
// blow up the handler.
interface WireFrame {
  store?: unknown;
  operation?: unknown;
  key?: unknown;
  crc32c?: unknown;
  owner?: unknown;
}

// Backoff bounds. Picked to recover quickly from a transient drop while
// avoiding a reconnect storm against a hard-down data-api revision.
const RECONNECT_INITIAL_MS = 1000;
const RECONNECT_MAX_MS = 30000;
// 5-second grace period after the last unsubscribe before tearing the
// socket down. Tabs that re-subscribe within that window (a common SPA
// re-render pattern) don't pay the reconnect tax.
const CLOSE_GRACE_MS = 5000;
// STABLE_OPEN_MS is how long a socket must stay open before we trust it
// enough to reset the backoff counter (FE9). The bare `open` event fires the
// instant a TCP/TLS+upgrade handshake completes, even for a backend that
// immediately RSTs the connection — resetting `attempt` there makes an
// accept-then-drop flap reconnect tightly (1s) forever instead of backing
// off. We instead reset on the FIRST FRAME (a real message proves the server
// is actually talking) or after the socket has been open this long without
// dropping, whichever comes first.
const STABLE_OPEN_MS = 5000;
// Session-probe cadence. The first probe fires on the
// PROBE_AFTER_FAILED_HANDSHAKES-th consecutive failed handshake (~7s into a
// streak at the backoff above, so an ended session costs 3 metered upgrades
// before it parks). While a streak outlives a non-401 answer — the session is
// fine and the socket fails for some other reason — the bus re-probes every
// PROBE_REPEAT_FAILED_HANDSHAKES further failures (~10 min at the 30s cap), so
// a session that ends mid-outage is still caught without a probe per retry.
const PROBE_AFTER_FAILED_HANDSHAKES = 3;
const PROBE_REPEAT_FAILED_HANDSHAKES = 20;

// SessionSignals is the bus's view of the page session (heartbeat.ts in
// production). probe() sends one heartbeat and resolves its HTTP status (0 =
// no answer); it never rejects. onResult() observes every beat's outcome — the
// D4 lifecycle's hourly and →visible beats and probe() answers alike — and
// returns an unregister function. Injectable so the unit tests can script it.
export interface SessionSignals {
  probe(): Promise<number>;
  onResult(listener: (status: number) => void): () => void;
}

const heartbeatSession: SessionSignals = {
  probe: sendHeartbeat,
  onResult: onHeartbeatResult,
};

// RemoteEventBus is exported for the SDK unit tests (FE9 backoff, session
// probe + park). It is not re-exported from the package entry, so it stays
// internal to the SDK build.
export class RemoteEventBus {
  private handlers = new Set<StorageChangeHandler>();
  private ws: WebSocket | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private closeTimer: ReturnType<typeof setTimeout> | null = null;
  // stableOpenTimer fires STABLE_OPEN_MS after a bare `open` and resets the
  // backoff counter — but only if the socket survives that long (FE9). A
  // close before it fires cancels it, so a flapping socket keeps its growing
  // backoff.
  private stableOpenTimer: ReturnType<typeof setTimeout> | null = null;
  private attempt = 0;
  // Set by close() (normal exit) so the close event handler can tell a
  // user-requested teardown apart from a transport drop.
  private intentionalClose = false;
  // socketStable is true once the current socket passed the FE9 stable point
  // (a frame, or STABLE_OPEN_MS open). A close before that is a failed
  // handshake: an upgrade the server rejected, or an accept-then-drop.
  private socketStable = false;
  // failedHandshakes counts consecutive failed handshakes (a stable socket
  // resets it); nextProbeAt is the count at which the next session probe fires.
  private failedHandshakes = 0;
  private nextProbeAt = PROBE_AFTER_FAILED_HANDSHAKES;
  // probeSeq tags the latest probe, so a superseded probe's answer is ignored.
  private probeSeq = 0;
  // parked: a probe answered 401, so the session has ended and no reconnect is
  // scheduled until a heartbeat succeeds. It deliberately outlives closeNow —
  // it describes the session, not the subscription — so a re-subscribe waits
  // for the same signal instead of spending metered upgrades rediscovering it.
  private parked = false;
  // unwatchSession tears down the resume watchers armed while parked.
  private unwatchSession: (() => void) | null = null;
  private readonly session: SessionSignals;

  constructor(session: SessionSignals = heartbeatSession) {
    this.session = session;
  }

  subscribe(handler: StorageChangeHandler): Unsubscribe {
    this.handlers.add(handler);
    this.cancelCloseTimer();
    this.ensureOpen();
    let unsubscribed = false;
    return () => {
      if (unsubscribed) return;
      unsubscribed = true;
      this.handlers.delete(handler);
      if (this.handlers.size === 0) {
        this.scheduleClose();
      }
    };
  }

  // ensureOpen opens the socket if it isn't already open or opening,
  // and isn't pending a reconnect. Reconnects are managed by the close
  // event handler — we don't double-up here.
  private ensureOpen(): void {
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }
    if (this.reconnectTimer) {
      // A reconnect is already queued. ensureOpen is called eagerly on
      // every subscribe; that's idempotent.
      return;
    }
    if (this.parked) {
      // The session has ended: an upgrade now would only be rejected — and
      // metered. A new subscriber waits for the resume signal like the rest.
      return;
    }
    this.openSocket();
  }

  private openSocket(): void {
    this.intentionalClose = false;
    this.socketStable = false;
    const proto = (typeof window !== "undefined" && window.location.protocol === "https:") ? "wss:" : "ws:";
    const host = (typeof window !== "undefined") ? window.location.host : "";
    const url = `${proto}//${host}/_backlit/events`;

    let sock: WebSocket;
    try {
      sock = new WebSocket(url);
    } catch {
      // Construction itself failed (rare — usually a malformed URL).
      // Schedule a reconnect to mirror the close-event path.
      this.scheduleReconnect();
      return;
    }
    this.ws = sock;

    sock.addEventListener("open", () => {
      // Do NOT reset attempt here (FE9): a bare `open` only means the
      // handshake completed, not that the connection is durable. Arm a timer
      // that resets the backoff once the socket has stayed open long enough
      // to be trustworthy. A drop before then cancels it (see close handler).
      this.armStableOpenTimer();
    });

    sock.addEventListener("message", (ev) => {
      // A real frame proves the server is talking — the connection is
      // genuinely up, so reset the backoff immediately (FE9).
      this.markStablyOpen();
      // Browsers may deliver text frames as Blob if binaryType is
      // misconfigured; force a string check. Server only sends text
      // frames.
      if (typeof ev.data !== "string") return;
      let raw: unknown;
      try {
        raw = JSON.parse(ev.data);
      } catch {
        return;
      }
      const event = parseFrame(raw);
      if (!event) return;
      for (const o of Array.from(passiveObservers)) {
        try {
          o(event);
        } catch {
          /* an observer never breaks delivery */
        }
      }
      this.dispatch(event);
    });

    sock.addEventListener("close", () => {
      // A socket closeNow already detached — or one a re-subscribe has since
      // replaced — must not clobber the live socket or queue a second
      // reconnect. Browsers report close asynchronously, so this happens.
      if (this.ws !== sock) return;
      this.ws = null;
      // The socket dropped before it was deemed stable: cancel the pending
      // reset so this flap keeps (and grows) its backoff (FE9).
      this.cancelStableOpenTimer();
      const wasStable = this.socketStable;
      this.socketStable = false;
      if (this.intentionalClose) {
        // Caller asked for the teardown; don't reconnect.
        return;
      }
      // Closed before it was deemed stable: a failed handshake. The streak
      // count decides when to probe the session.
      if (!wasStable) this.failedHandshakes++;
      if (this.handlers.size === 0) {
        // Nobody cares anymore.
        return;
      }
      this.scheduleReconnect();
      if (!wasStable && this.failedHandshakes >= this.nextProbeAt) {
        this.probeSession();
      }
    });

    // We deliberately don't add an "error" listener for reconnect — the
    // close event fires after every error, and treating both as
    // signals would double-schedule.
  }

  // dispatch delivers ev to the registered handlers (never the passive
  // observers). It opens nothing.
  dispatch(ev: StorageChangeEvent): void {
    // Snapshot handlers before iterating so a handler that calls
    // unsubscribe() doesn't shift the live Set under us.
    for (const h of Array.from(this.handlers)) {
      try {
        h(ev);
      } catch (e) {
        // Same isolation policy as the Go-side hub: a panicking
        // handler doesn't kill its siblings. Log once and continue.
        // eslint-disable-next-line no-console
        console.error("[backlit] storage.change handler threw", e);
      }
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) return;
    const delay = backoffDelay(this.attempt);
    this.attempt++;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.handlers.size === 0) return;
      this.openSocket();
    }, delay);
  }

  // probeSession asks the session whether it is still alive, alongside the
  // reconnect scheduleReconnect just queued — never holding it back, so a slow
  // or hung probe degrades to exactly the old behaviour. A 401 parks the loop;
  // any other answer (2xx, 429, 5xx, 0 = no answer) leaves today's backoff be.
  private probeSession(): void {
    this.nextProbeAt = this.failedHandshakes + PROBE_REPEAT_FAILED_HANDSHAKES;
    const seq = ++this.probeSeq;
    void this.askSession().then((status) => {
      if (seq !== this.probeSeq || status !== 401) return;
      if (this.ws === null) {
        this.park();
      } else {
        // The answer outlasted the backoff: the next attempt is already in
        // flight. Let it resolve (a stable socket ends the streak), but
        // re-probe on its failure rather than REPEAT failures from now.
        this.nextProbeAt = this.failedHandshakes + 1;
      }
    });
  }

  // askSession runs one probe, flattening a throw or rejection to 0.
  private askSession(): Promise<number> {
    try {
      return this.session.probe().catch(() => 0);
    } catch {
      return Promise.resolve(0);
    }
  }

  // park stops the loop for an ended session: cancel the queued reconnect and
  // wait for a heartbeat to prove the session alive again. Handlers stay
  // registered. The first failure after a resume re-probes straight away.
  private park(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.parked = true;
    this.nextProbeAt = this.failedHandshakes + 1;
    this.watchSession();
  }

  // watchSession arms the resume signals while parked: any successful
  // heartbeat, and a fresh probe on each →visible transition (D4 precedent) —
  // which joins the lifecycle's own →visible beat (single-flight) rather than
  // adding a request. The probe's answer arrives through onResult too.
  private watchSession(): void {
    if (this.unwatchSession) return;
    const offResult = this.session.onResult((status) => {
      if (status >= 200 && status < 300) this.resume();
    });
    const hasDocument = typeof document !== "undefined";
    const onVisibility = () => {
      if (!document.hidden) void this.askSession();
    };
    if (hasDocument) document.addEventListener("visibilitychange", onVisibility);
    this.unwatchSession = () => {
      offResult();
      if (hasDocument) document.removeEventListener("visibilitychange", onVisibility);
    };
  }

  // resume ends a park once a heartbeat proved the session alive, reconnecting
  // at once: the backoff delay belonged to the ended session, not this one.
  private resume(): void {
    if (!this.parked) return;
    this.parked = false;
    if (this.unwatchSession) {
      this.unwatchSession();
      this.unwatchSession = null;
    }
    if (this.handlers.size > 0) this.ensureOpen();
  }

  // attemptForTests exposes the private backoff counter. Test-only.
  attemptForTests(): number {
    return this.attempt;
  }

  // armStableOpenTimer schedules the backoff reset for STABLE_OPEN_MS in the
  // future. Idempotent: a second `open` (shouldn't happen on one socket)
  // won't stack timers.
  private armStableOpenTimer(): void {
    if (this.stableOpenTimer) return;
    this.stableOpenTimer = setTimeout(() => {
      this.stableOpenTimer = null;
      // Still connected after the grace window — trust it.
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.markStable();
      }
    }, STABLE_OPEN_MS);
  }

  // markStablyOpen resets the backoff now (a frame arrived) and cancels the
  // pending grace timer since it's no longer needed.
  private markStablyOpen(): void {
    this.cancelStableOpenTimer();
    this.markStable();
  }

  // markStable records that the current socket is genuinely up: reset the
  // backoff (FE9) and end any failed-handshake streak — superseding a probe
  // still in flight, whose late 401 must not park a loop that just recovered.
  private markStable(): void {
    this.socketStable = true;
    this.attempt = 0;
    this.failedHandshakes = 0;
    this.nextProbeAt = PROBE_AFTER_FAILED_HANDSHAKES;
    this.probeSeq++;
  }

  private cancelStableOpenTimer(): void {
    if (this.stableOpenTimer) {
      clearTimeout(this.stableOpenTimer);
      this.stableOpenTimer = null;
    }
  }

  private scheduleClose(): void {
    if (this.closeTimer) return;
    this.closeTimer = setTimeout(() => {
      this.closeTimer = null;
      if (this.handlers.size > 0) return;
      this.closeNow();
    }, CLOSE_GRACE_MS);
  }

  private cancelCloseTimer(): void {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  private closeNow(): void {
    this.intentionalClose = true;
    this.cancelStableOpenTimer();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      try {
        this.ws.close(1000, "no subscribers");
      } catch {
        // Ignore; close() can throw if the socket is in an invalid
        // state, but we're already tearing down.
      }
      this.ws = null;
    }
    this.socketStable = false;
    this.attempt = 0;
    this.failedHandshakes = 0;
    this.nextProbeAt = PROBE_AFTER_FAILED_HANDSHAKES;
    // `parked` (and its resume watchers) deliberately survive — see the field.
  }
}

// backoffDelay computes the reconnect delay for a given attempt count:
// exponential growth (RECONNECT_INITIAL_MS * 2^attempt) capped at
// RECONNECT_MAX_MS, with ±25% jitter, floored at RECONNECT_INITIAL_MS.
// rand is injectable for deterministic tests; production uses Math.random.
// Exported (test-only consumers) so the growth + jitter contract can be
// asserted without driving real timers.
export function backoffDelay(attempt: number, rand: () => number = Math.random): number {
  const base = Math.min(RECONNECT_MAX_MS, RECONNECT_INITIAL_MS * Math.pow(2, attempt));
  const jitter = base * (rand() * 0.5 - 0.25);
  return Math.max(RECONNECT_INITIAL_MS, base + jitter);
}

// parseFrame is exported for the SDK unit tests (records frame + owner). Like
// backoffDelay/RemoteEventBus it is not re-exported from the package entry, so
// it stays internal to the SDK build.
export function parseFrame(raw: unknown): StorageChangeEvent | null {
  if (raw === null || typeof raw !== "object") return null;
  const w = raw as WireFrame;
  if (w.store !== "data" && w.store !== "userdata" && w.store !== "records") return null;
  if (w.operation !== "set" && w.operation !== "delete") return null;
  if (typeof w.key !== "string" || w.key.length === 0) return null;
  if (typeof w.crc32c !== "string") return null;
  if (w.store === "records") {
    // records events carry the owner uid (tolerate-missing → "").
    return {
      store: w.store,
      operation: w.operation,
      key: w.key,
      crc32c: w.crc32c,
      owner: typeof w.owner === "string" ? w.owner : "",
    };
  }
  return {
    store: w.store,
    operation: w.operation,
    key: w.key,
    crc32c: w.crc32c,
  };
}

// Lazy singleton. The first import doesn't open a socket — only the
// first onStorageChange call does.
let cached: RemoteEventBus | null = null;
function bus(): RemoteEventBus {
  if (!cached) cached = new RemoteEventBus();
  return cached;
}

export function remoteOnStorageChange(handler: StorageChangeHandler): Unsubscribe {
  return bus().subscribe(handler);
}

// dispatchLocalChange delivers a local storage.change (a write queued on this
// device, tagged pending — backlit.offline, D5) to this page's on() handlers.
// It never builds the bus or opens the socket (no subscriber, no bus, nobody
// to tell) and never reaches the passive observers.
export function dispatchLocalChange(ev: StorageChangeEvent): void {
  cached?.dispatch(ev);
}
